import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import { IconButton, Tooltip } from "@material-ui/core";
import useNow from "../../hooks/useNow";
import { getWaitingState, sortWaitingTickets } from "../../utils/waitingTime";
import WaitingStatus, { WaitingLegend, useWaitingStyles } from "../WaitingStatus";
import TicketPreviewModal from "../TicketPreviewModal";
import ContactAvatar from "../ContactAvatar";
import { contactDisplayName, updateTicketContacts, mergeContact } from "../../utils/contactPresentation";
import ExactTime from "../ExactTime";
import { toast } from "react-toastify";

import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
	"@keyframes cardIn": {
		from: { opacity: 0, transform: "translateY(10px) scale(.97)" },
		to: { opacity: 1, transform: "translateY(0) scale(1)" },
	},
	"@keyframes dropGlow": {
		"0%": { boxShadow: "0 0 0 0 rgba(16,185,129,.45)" },
		"70%": { boxShadow: "0 0 0 10px rgba(16,185,129,0)" },
		"100%": { boxShadow: "0 0 0 0 rgba(16,185,129,0)" },
	},
	root: {
		display: "flex",
		flexDirection: "column",
		height: "100%",
		overflow: "hidden",
	},
	hint: {
		fontSize: "0.78rem",
		color: theme.palette.text.secondary,
		marginBottom: 12,
		flex: "none",
	},
	board: {
		flex: 1,
		display: "flex",
		gap: 12,
		paddingBottom: 8,
		overflowX: "auto",
		overflowY: "hidden",
		alignItems: "stretch",
		minHeight: 320,
		...theme.scrollbarStyles,
	},
	column: {
		width: 300,
		minWidth: 300,
		display: "flex",
		flexDirection: "column",
		borderRadius: 14,
		border: `1px solid ${theme.custom.border}`,
		backgroundColor: theme.palette.background.default,
		transition:
			"border-color .2s ease, box-shadow .2s ease, transform .2s ease, background-color .2s ease",
		overflow: "hidden",
	},
	columnDragOver: {
		borderColor: "#10B981",
		boxShadow:
			"0 0 0 3px rgba(16,185,129,.28), 0 14px 34px rgba(16,185,129,.22)",
		backgroundColor:
			theme.palette.type === "dark"
				? "rgba(16,185,129,.08)"
				: "rgba(16,185,129,.06)",
		transform: "translateY(-3px)",
		animation: "$dropGlow 1.4s ease-out infinite",
	},
	columnMine: {
		borderColor: theme.palette.primary.main,
	},
	columnHeader: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		padding: "10px 12px",
		flex: "none",
		borderBottom: `1px solid ${theme.custom.border}`,
		backgroundColor: theme.custom.surface,
	},
	columnAvatar: {
		width: 30,
		height: 30,
		fontSize: "0.75rem",
		fontWeight: 700,
		backgroundColor: theme.palette.primary.main,
		color: "#fff",
	},
	pendingAvatar: {
		width: 30,
		height: 30,
		backgroundColor: "rgba(245,158,11,.15)",
		color: "#F59E0B",
	},
	columnTitle: {
		flex: 1,
		fontWeight: 700,
		fontSize: "0.85rem",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	countChip: {
		height: 20,
		fontSize: "0.7rem",
	},
	cardsArea: {
		flex: 1,
		overflowY: "auto",
		padding: 8,
		...theme.scrollbarStyles,
	},
	card: {
		position: "relative",
		"&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
		"@media (prefers-reduced-motion: reduce)": { animation: "none", transition: "none", "&:hover": { transform: "none" } },
		display: "flex",
		alignItems: "flex-start",
		gap: 9,
		padding: "12px 10px",
		marginBottom: 10,
		borderRadius: 12,
		border: `1px solid ${theme.custom.border}`,
		backgroundColor: theme.custom.surface,
		cursor: "grab",
		userSelect: "none",
		animation: "$cardIn .35s cubic-bezier(.2,.9,.3,1.1) both",
		transition:
			"box-shadow .2s ease, opacity .2s ease, transform .2s cubic-bezier(.2,.9,.3,1.2)",
		"&:hover": {
			transform: "translateY(-2px)",
			boxShadow:
				theme.palette.type === "dark"
					? "0 6px 16px rgba(0,0,0,.55)"
					: "0 6px 16px rgba(15,23,42,.16)",
		},
		"&:active": {
			cursor: "grabbing",
		},
	},
	// o card original vira um "vazio" tracejado enquanto o clone flutua
	cardGhost: {
		opacity: 0.35,
		border: `1px dashed rgba(16,185,129,.7)`,
		"& > *": { visibility: "hidden" },
	},
	// clone que segue o ponteiro: levanta, inclina e ganha sombra
	floatingCard: {
		position: "fixed",
		zIndex: 3000,
		pointerEvents: "none",
		display: "flex",
		alignItems: "center",
		gap: 10,
		padding: "8px 10px",
		borderRadius: 12,
		border: "1px solid #10B981",
		backgroundColor: theme.custom.surface,
		transform: "scale(1.06) rotate(2.5deg)",
		boxShadow: "0 18px 40px rgba(2,6,23,.4)",
	},
	cardAvatar: {
		width: 34,
		height: 34,
	},
	cardBody: {
		flex: 1,
		minWidth: 0,
	},
	cardName: {
		paddingRight: 13,
		fontWeight: 600,
		fontSize: "0.82rem",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	cardMessage: {
		fontSize: "0.75rem",
		color: theme.palette.text.secondary,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	cardTime: { display: "block", fontSize: ".63rem", color: theme.palette.text.secondary, marginTop: 3, fontVariantNumeric: "tabular-nums" },
	detailsButton: { position: "absolute", right: 5, top: 6, color: theme.palette.text.secondary },
	queueDot: {
		width: 6,
		height: 6,
		borderRadius: "50%",
		position: "absolute",
		top: 53,
		left: 24,
	},
	emptyColumn: {
		margin: 4,
		padding: "20px 10px",
		textAlign: "center",
		fontSize: "0.76rem",
		color: theme.palette.text.secondary,
		border: `1px dashed ${
			theme.palette.type === "dark"
				? "rgba(148,163,184,.45)"
				: "rgba(148,163,184,.6)"
		}`,
		borderRadius: 10,
		transition: "all .2s ease",
	},
	countChipActive: {
		backgroundColor: "#10B981",
		color: "#fff",
		fontWeight: 800,
	},
	loading: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		minHeight: 220,
	},
}));

const TicketsTeamView = ({ selectedQueueIds }) => {
	const classes = useStyles();
	const history = useHistory();
	const waitingClasses = useWaitingStyles();
	const [previewTicketId, setPreviewTicketId] = useState(null);
	const { user } = useContext(AuthContext);
	const [users, setUsers] = useState([]);
	const [tickets, setTickets] = useState([]);
	const [loading, setLoading] = useState(true);
	const [drag, setDrag] = useState(null); // {ticketId, x, y, dx, dy, w}
	const [dragOverColumn, setDragOverColumn] = useState(null);
	const refreshTimer = useRef(null);
	const startRef = useRef(null);
	const now = useNow(tickets.some(ticket => ticket.status === "pending"));

	// A chave serializada mantem fetchBoard estavel: quem chama costuma montar um
	// array novo a cada render, o que refazia a busca e reabria o socket sem parar.
	const queueIdsKey = JSON.stringify(selectedQueueIds || []);

	const fetchBoard = useCallback(async () => {
		try {
			const { data } = await api.get("/tickets/team-board", {
				params: { queueIds: queueIdsKey },
			});
			setTickets(current => data.tickets.map(ticket => ({ ...ticket, contact: mergeContact(current.find(item => item.id === ticket.id)?.contact, ticket.contact) })));
			setUsers(data.users);
			setLoading(false);
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	}, [queueIdsKey]);

	useEffect(() => {
		setLoading(true);
		fetchBoard();
	}, [fetchBoard]);

	useEffect(() => {
		const socket = openSocket();

		socket.on("connect", () => {
			socket.emit("joinTickets", "open");
			socket.emit("joinTickets", "pending");
		});

		const scheduleRefresh = () => {
			clearTimeout(refreshTimer.current);
			refreshTimer.current = setTimeout(fetchBoard, 400);
		};

		socket.on("ticket", scheduleRefresh);
		socket.on("appMessage", scheduleRefresh);
		socket.on("contact", data => {
			if (data.action === "update" && data.contact) setTickets(current => updateTicketContacts(current, data.contact));
		});

		return () => {
			clearTimeout(refreshTimer.current);
			socket.disconnect();
		};
	}, [fetchBoard]);

	const handleDrop = async (columnUserId, ticketId) => {
		setDragOverColumn(null);
		if (!ticketId) return;

		const ticket = tickets.find((t) => t.id === ticketId);
		if (!ticket) return;

		const targetUserId = columnUserId === "pending" ? null : columnUserId;
		const currentUserId = ticket.status === "pending" ? null : ticket.userId;
		if (targetUserId === currentUserId) return;

		// otimista: move o card na hora
		setTickets((prev) =>
			prev.map((t) =>
				t.id === ticketId
					? {
							...t,
							userId: targetUserId,
							status: targetUserId ? "open" : "pending",
							waitingSince: targetUserId ? null : new Date().toISOString(),
							waitingSinceEstimated: false,
					}
					: t
			)
		);

		try {
			await api.put(`/tickets/${ticketId}`, {
				status: targetUserId ? "open" : "pending",
				userId: targetUserId,
			});
			fetchBoard();
			const targetUser = users.find((u) => u.id === targetUserId);
			toast.success(
				targetUser
					? `${i18n.t("ticketsTeam.transferredTo")} ${targetUser.name}`
					: i18n.t("ticketsTeam.returnedToQueue")
			);
		} catch (err) {
			toastError(err);
			fetchBoard();
		}
	};

	const initials = (name) =>
		(name || "?")
			.split(" ")
			.map((n) => n[0])
			.slice(0, 2)
			.join("")
			.toUpperCase();

	// drag manual com pointer events: o card segue o ponteiro (mouse ou
	// dedo) inclinado e com sombra — a mesma sensação da demonstração
	const columnAt = (x, y) => {
		const els = document.elementsFromPoint(x, y);
		const col = els.find((el) => el.dataset && el.dataset.colId);
		return col ? col.dataset.colId : null;
	};

	const onCardPointerDown = (e, ticket) => {
		if (e.target.closest("button")) return;
		if (e.pointerType === "mouse" && e.button !== 0) return;
		const rect = e.currentTarget.getBoundingClientRect();
		startRef.current = {
			ticketId: ticket.id,
			x0: e.clientX,
			y0: e.clientY,
			dx: e.clientX - rect.left,
			dy: e.clientY - rect.top,
			w: rect.width,
			active: false,
		};
		e.currentTarget.setPointerCapture(e.pointerId);
	};

	const onCardPointerMove = (e) => {
		const st = startRef.current;
		if (!st) return;
		if (!st.active) {
			if (
				Math.abs(e.clientX - st.x0) + Math.abs(e.clientY - st.y0) < 7
			)
				return;
			st.active = true;
		}
		setDrag({
			ticketId: st.ticketId,
			x: e.clientX,
			y: e.clientY,
			dx: st.dx,
			dy: st.dy,
			w: st.w,
		});
		const col = columnAt(e.clientX, e.clientY);
		setDragOverColumn((prev) => (prev === col ? prev : col));
	};

	const onCardPointerUp = (e) => {
		const st = startRef.current;
		startRef.current = null;
		if (!st) return;
		if (!st.active) {
			// clique simples: abre o ticket
			history.push(`/tickets/${st.ticketId}`);
			return;
		}
		const col = columnAt(e.clientX, e.clientY);
		setDrag(null);
		if (col) {
			handleDrop(col === "pending" ? "pending" : Number(col), st.ticketId);
		} else {
			setDragOverColumn(null);
		}
	};

	const cardInner = (ticket) => (
		<>
			<ContactAvatar className={classes.cardAvatar} contact={ticket.contact} />
			<div className={classes.cardBody}>
				<Typography className={classes.cardName}>
					{contactDisplayName(ticket.contact)}
				</Typography>
				<Typography className={classes.cardMessage}>
					{ticket.lastMessage || ""}
				</Typography>
				<ExactTime value={ticket.lastMessageAt} className={classes.cardTime} />
				<WaitingStatus ticket={ticket} now={now} />
			</div>
			<Tooltip title="Ver detalhes do atendimento" arrow>
				<IconButton size="small" className={classes.detailsButton} aria-label={`Ver detalhes de ${ticket.contact?.name || "atendimento"}`} onPointerDown={event => event.stopPropagation()} onPointerUp={event => event.stopPropagation()} onClick={event => { event.stopPropagation(); setPreviewTicketId(ticket.id); }}>
					<VisibilityOutlinedIcon style={{ fontSize: 16 }} />
				</IconButton>
			</Tooltip>
			<span
				className={classes.queueDot}
				style={{ backgroundColor: ticket.queue?.color || "#94A3B8" }}
				title={ticket.queue?.name || i18n.t("ticketsTeam.noQueue")}
			/>
		</>
	);

	const renderCard = (ticket) => (
		<div
			key={ticket.id}
			className={`${classes.card} ${ticket.status === "pending" ? `${waitingClasses.frame} ${waitingClasses[getWaitingState(ticket, now).level]}` : ""} ${
				drag?.ticketId === ticket.id ? classes.cardGhost : ""
			}`}
			style={{ touchAction: "none" }}
			role="group"
			aria-label={`Atendimento de ${ticket.contact?.name || "contato"}`}
			tabIndex={0}
			onKeyDown={event => { if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); setPreviewTicketId(ticket.id); } }}
			onPointerDown={(e) => onCardPointerDown(e, ticket)}
			onPointerMove={onCardPointerMove}
			onPointerUp={onCardPointerUp}
			onPointerCancel={() => {
				startRef.current = null;
				setDrag(null);
				setDragOverColumn(null);
			}}
		>
			{cardInner(ticket)}
		</div>
	);

	const renderColumn = (key, title, avatar, columnTickets, isMine) => {
		const isOver = String(dragOverColumn) === String(key);
		return (
			<div
				key={key}
				data-col-id={key}
				className={`${classes.column} ${isOver ? classes.columnDragOver : ""} ${
					isMine && !isOver ? classes.columnMine : ""
				}`}
			>
				<div className={classes.columnHeader}>
					{avatar}
					<Typography className={classes.columnTitle}>
						{title}
						{isMine ? ` (${i18n.t("ticketsTeam.you")})` : ""}
					</Typography>
					<Chip
						size="small"
						className={`${classes.countChip} ${
							columnTickets.length > 0 ? classes.countChipActive : ""
						}`}
						label={columnTickets.length}
					/>
				</div>
				<div className={classes.cardsArea}>
					{columnTickets.length === 0 ? (
						<div className={classes.emptyColumn}>
							{i18n.t("ticketsTeam.dropHere")}
						</div>
					) : (
						columnTickets.map(renderCard)
					)}
				</div>
			</div>
		);
	};

	if (loading && tickets.length === 0) {
		return (
			<div className={classes.loading}>
				<CircularProgress size={28} />
			</div>
		);
	}

	const pendingTickets = sortWaitingTickets(tickets.filter((t) => t.status === "pending"));

	// o proprio usuario aparece primeiro, depois os demais em ordem alfabetica
	const orderedUsers = [...users].sort((a, b) => {
		if (a.id === user?.id) return -1;
		if (b.id === user?.id) return 1;
		return 0;
	});

	return (
		<div className={classes.root}>
			<Typography className={classes.hint}>
				{i18n.t("ticketsTeam.hint")}
			</Typography>
			<WaitingLegend />
			<TicketPreviewModal open={Boolean(previewTicketId)} ticket={tickets.find(ticket => ticket.id === previewTicketId)} onClose={() => setPreviewTicketId(null)} />
			<div className={classes.board}>
				{renderColumn(
					"pending",
					i18n.t("ticketsTeam.waitingColumn"),
					<Avatar className={classes.pendingAvatar}>
						<HourglassEmptyIcon style={{ fontSize: 16 }} />
					</Avatar>,
					pendingTickets,
					false
				)}
				{orderedUsers.map((u) =>
					renderColumn(
						u.id,
						u.name,
						<Avatar className={classes.columnAvatar}>
							{initials(u.name)}
						</Avatar>,
						tickets.filter(
							(t) => t.status === "open" && t.userId === u.id
						),
						u.id === user?.id
					)
				)}
			</div>

			{/* clone flutuante que segue o ponteiro durante o arrasto */}
			{drag &&
				(() => {
					const t = tickets.find((x) => x.id === drag.ticketId);
					if (!t) return null;
					return (
						<div
							className={classes.floatingCard}
							style={{
								left: drag.x - drag.dx,
								top: drag.y - drag.dy,
								width: drag.w,
							}}
						>
							{cardInner(t)}
						</div>
					);
				})()}
		</div>
	);
};

export default TicketsTeamView;
